import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles';
import {
  buildDateFormatter,
  generateClaimTitle,
  getClaimPhaseTypeHeaderText,
  getFailedSubmissionsWithinLast30Days,
  getListItemLabel,
  getShowEightPhases,
  getStatusDescription,
} from '~/platform/shared/claims-appeals';
import { claimTabPath } from '../utils/claim-tab-helpers';
import ClaimCard from './ClaimCard';

const formatDate = buildDateFormatter();

const getLastUpdated = (claim, listCardMeta = {}) => {
  const { claimPhaseDates, claimStatusMeta, status, vesStatusUpdatedDate } =
    claim.attributes;
  const phaseChangeDate = claimPhaseDates?.phaseChangeDate;
  const currentStep = claimStatusMeta?.overview?.currentStepByStatus?.[status];
  // Use vesStatusUpdatedDate only when the claim is in Step 2 (decided); fall back to
  // phaseChangeDate for Step 1 or if the field is set before a phase revert.
  const dateToUse =
    currentStep === 2 && vesStatusUpdatedDate
      ? vesStatusUpdatedDate
      : phaseChangeDate;
  const updatedOn = dateToUse ? formatDate(dateToUse) : null;
  if (!updatedOn || updatedOn === 'Invalid date') return null;

  const prefix = listCardMeta.lastUpdatedPrefix || 'Moved to this step on';
  return `${prefix} ${updatedOn}`;
};

const isClaimComplete = claim =>
  claim.attributes.status?.toLowerCase() === 'complete' ||
  claim.attributes.status === 'vbms';

const showPreDecisionCommunications = claim => {
  const { decisionLetterSent } = claim.attributes;

  return !decisionLetterSent && !isClaimComplete(claim);
};

const CommunicationsLine = ({ children, icon }) => {
  return (
    <div className="vads-u-display--flex vads-u-align-items--center">
      <va-icon
        icon={icon}
        size={3}
        class="vads-u-margin-right--1"
        aria-hidden="true"
      />
      <p className="vads-u-margin--0">{children}</p>
    </div>
  );
};

CommunicationsLine.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.string.isRequired,
};

export default function ClaimsListItem({ claim }) {
  const {
    claimDate,
    claimPhaseDates,
    claimTypeCode,
    claimStatusMeta,
    decisionLetterSent,
    documentsNeeded,
    status,
    evidenceSubmissions = [],
  } = claim.attributes || {};

  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const cstClaimPhasesEnabled = useToggleValue(TOGGLE_NAMES.cstClaimPhases);
  const useVaTabs = useToggleValue(TOGGLE_NAMES.cstClaimDetailsVaTabs);
  const cstMultiClaimProviderEnabled = useToggleValue(
    TOGGLE_NAMES.cstMultiClaimProvider,
  );
  const showEightPhases = getShowEightPhases(
    claimTypeCode,
    cstClaimPhasesEnabled,
  );
  const listCardMeta = claimStatusMeta?.listCard || {};
  const { decisionLetterLabel } = listCardMeta;
  const stepContent = showEightPhases
    ? claimStatusMeta?.whatWeAreDoing?.phaseTypeMap?.[
        claimPhaseDates?.phaseType
      ]
    : claimStatusMeta?.whatWeAreDoing?.statusMap?.[status];

  const label = getListItemLabel(claim);
  const showPrecomms = showPreDecisionCommunications(claim);
  const formattedReceiptDate = formatDate(claimDate);
  const humanStatus =
    stepContent?.title ||
    (showEightPhases
      ? getClaimPhaseTypeHeaderText(claimPhaseDates.phaseType)
      : getStatusDescription(status));
  const cardTitle = listCardMeta.title || generateClaimTitle(claim);
  const receivedLabel = listCardMeta.receivedLabel || 'Received on';
  const lastUpdated = getLastUpdated(claim, listCardMeta);
  const showAlert = showPrecomms && documentsNeeded;

  const ariaLabel = `Details for ${cardTitle} submitted on ${formattedReceiptDate}`;
  const provider = cstMultiClaimProviderEnabled
    ? claim.attributes?.provider
    : null;
  const legacyHref = provider
    ? `/your-claims/${claim.id}/status?type=${provider}`
    : `/your-claims/${claim.id}/status`;
  const href = useVaTabs
    ? claimTabPath(claim.id, 'status', {
        search: provider ? `?type=${provider}` : '',
      })
    : legacyHref;

  // Memoize failed submissions so the derived showActionTag value is stable across
  // renders rather than recomputed from a new array reference each time
  const failedSubmissionsWithinLast30Days = useMemo(
    () => getFailedSubmissionsWithinLast30Days(evidenceSubmissions),
    [evidenceSubmissions],
  );
  const showActionTag =
    showAlert || failedSubmissionsWithinLast30Days.length > 0;

  return (
    <ClaimCard
      title={cardTitle}
      label={label}
      subtitle={`${receivedLabel} ${formattedReceiptDate}`}
    >
      {decisionLetterSent &&
        (!['ivc_champva', 'ivcchampvabenefitsclaimsprovider'].includes(
          claim.attributes?.provider,
        ) ||
          decisionLetterLabel) && (
          <CommunicationsLine icon="mail">
            {decisionLetterLabel || 'You have a decision letter ready'}
          </CommunicationsLine>
        )}
      <div className="card-status">
        {humanStatus && <p>{humanStatus}</p>}
        {lastUpdated && <p>{lastUpdated}</p>}
      </div>
      {showActionTag && (
        <va-tag-status status="warning" text="Action may be needed" />
      )}
      <ClaimCard.Link ariaLabel={ariaLabel} href={href} />
    </ClaimCard>
  );
}

ClaimsListItem.propTypes = {
  claim: PropTypes.object,
};
